import React from 'react';
import Seo from '../components/Seo';
import { productLd, breadcrumbLd, SITE } from '../lib/seo';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, FileText, MessageCircle, Check, Package, Share2 } from 'lucide-react';
import Reveal from '../components/Reveal';
import { products, categories, certifications } from '../mock/mock';
import { openWhatsApp } from '../lib/wa';

const ProductDetail = () => {
  const { slug } = useParams();
  const product = products.find((p) => p.slug === slug);
  const [copied, setCopied] = React.useState(false);

  const handleShare = async () => {
    const shareUrl = `${SITE.url}/api/share/product/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: product.name, url: shareUrl });
        return;
      }
    } catch (e) { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard blocked */ }
  };

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-ktc-green">Product not found</h1>
        <Link to="/products" className="inline-flex items-center gap-2 text-ktc-gold font-medium mt-6"><ArrowLeft size={16} /> Back to products</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.id === product.category);
  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 3);

  return (
    <div>
      <Seo
        title={product.name}
        description={product.short || product.description}
        path={`/products/${product.slug}`}
        image={`/og-product-${product.slug}.png`}
        jsonLd={[productLd(product, category?.name), breadcrumbLd([{ name: 'Home', path: '/' }, { name: 'Products', path: '/products' }, { name: product.name, path: `/products/${product.slug}` }])]}
      />
      {/* Breadcrumb */}
      <div className="bg-ktc-cream/50 border-b border-ktc-cream">
        <div className="max-w-7xl mx-auto px-6 py-4 text-sm text-ktc-charcoal/60 flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-ktc-green">Home</Link> /
          <Link to="/products" className="hover:text-ktc-green">Products</Link> /
          <Link to={`/products?category=${product.category}`} className="hover:text-ktc-green">{category?.name}</Link> /
          <span className="text-ktc-green font-medium">{product.name}</span>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-2 gap-12">
        <Reveal className="img-zoom rounded-2xl overflow-hidden shadow-xl h-fit">
          <img src={product.image} alt={product.name} className="w-full h-[460px] object-cover" />
        </Reveal>
        <Reveal delay={80}>
          <span className="text-[11px] uppercase tracking-wider text-ktc-gold font-semibold">{category?.name}</span>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-2">{product.name}</h1>
          <p className="text-ktc-charcoal/75 mt-5 leading-relaxed">{product.description}</p>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={`/quote?product=${encodeURIComponent(product.name)}`} className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-7 py-3.5 rounded-md transition-colors">
              <FileText size={18} /> Request a Quote
            </Link>
            <button type="button" onClick={() => openWhatsApp('Enquiry about ' + product.name)} data-testid="product-whatsapp-button" className="inline-flex items-center gap-2 border border-ktc-green/25 hover:border-ktc-green text-ktc-green font-medium px-7 py-3.5 rounded-md transition-colors">
              <MessageCircle size={18} /> WhatsApp
            </button>
            <button type="button" onClick={handleShare} data-testid="product-share-button" className="inline-flex items-center gap-2 border border-ktc-green/25 hover:border-ktc-green text-ktc-green font-medium px-7 py-3.5 rounded-md transition-colors">
              {copied ? <><Check size={18} /> Link Copied</> : <><Share2 size={18} /> Share</>}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-8">
            {certifications.slice(0, 4).map((c) => (
              <span key={c.abbr} className="text-xs bg-ktc-cream/70 text-ktc-green font-medium px-3 py-1.5 rounded-full">{c.abbr}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Specs & packaging */}
      <section className="max-w-7xl mx-auto px-6 pb-16 grid lg:grid-cols-2 gap-8">
        <div className="bg-ktc-ivoryLight rounded-xl border border-ktc-cream p-7">
          <h2 className="font-serif text-2xl font-semibold text-ktc-green mb-5">Specifications</h2>
          <dl className="divide-y divide-ktc-cream">
            {Object.entries(product.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between py-3 gap-4">
                <dt className="text-sm text-ktc-charcoal/60">{k}</dt>
                <dd className="text-sm font-medium text-ktc-green text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="bg-ktc-green text-ktc-ivory rounded-xl p-7">
          <h2 className="font-serif text-2xl font-semibold mb-5 flex items-center gap-2"><Package size={22} className="text-ktc-goldLight" /> Packaging Options</h2>
          <ul className="space-y-3.5">
            {product.packaging.map((p) => (
              <li key={p} className="flex gap-3 text-sm text-ktc-ivory/85">
                <Check size={18} className="text-ktc-goldLight shrink-0" /> {p}
              </li>
            ))}
          </ul>
          <p className="text-xs text-ktc-ivory/60 mt-6 border-t border-ktc-ivory/15 pt-5">Shipped FOB Port Qasim, Karachi. CIF pricing available to all major ports worldwide.</p>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-ktc-cream/50">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <h2 className="font-serif text-3xl font-semibold text-ktc-green mb-8">Related Products</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/products/${p.slug}`} className="group bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream card-lift">
                  <div className="img-zoom h-44"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
                  <div className="p-5">
                    <h3 className="font-serif text-xl font-semibold text-ktc-green">{p.name}</h3>
                    <span className="inline-flex items-center gap-1.5 text-ktc-gold font-medium text-sm mt-3 group-hover:gap-2.5 transition-all">View details <ArrowRight size={15} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
